/* eslint-disable */
// DelayReportModal — "Export Delay Report" dialog for the Developments portal.
// Matches export-delay-report.html (David approved). DNA tokens scoped to
// .exportRoot per Convention #68. Modal shell (backdrop + ESC + click-outside)
// via createPortal to document.body (Convention #126 pattern).
//
// Two modes: By Designer (all delays, side-tagged) / By Factory (factory-side
// chase-list only). Delay = time-in-stage > per-stage threshold (14/30/45).
// Excel via useDelayReportExport (SheetJS). Compute in delayReportUtils.

import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './DelayReportModal.module.css';
import { computeDelayRows, groupsFromRows, DEFAULT_THRESHOLDS } from './delayReportUtils';
import { useDelayReportExport } from './useDelayReportExport';

const ICON_CHECK = <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>;
const ICON_CLOSE = <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>;
const ICON_INFO = <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>;
const ICON_IMG = <svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>;
const ICON_DOWNLOAD = <svg viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>;

const STAGE_META = [
  { key: 'drawing', name: 'Drawing',      cls: 'siDraw',  icon: <path d="M12 19l7-7 3 3-7 7-3-3z M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" /> },
  { key: 'hand',    name: 'Hand Made',    cls: 'siHand',  icon: <><path d="M18 11V6a2 2 0 0 0-4 0M14 10V4a2 2 0 0 0-4 0v2M10 10.5V6a2 2 0 0 0-4 0v8" /><path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" /></> },
  { key: 'color',   name: 'Color Sample', cls: 'siColor', icon: <><circle cx="13.5" cy="6.5" r="2.5" /><circle cx="17.5" cy="10.5" r="2.5" /><circle cx="8.5" cy="7.5" r="2.5" /><circle cx="6.5" cy="12.5" r="2.5" /><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.93 0 1.65-.75 1.65-1.69 0-.44-.18-.83-.44-1.12-.29-.29-.44-.65-.44-1.12a1.64 1.64 0 0 1 1.67-1.67h1.99c3.05 0 5.55-2.5 5.55-5.55C21.97 6.01 17.46 2 12 2z" /></> },
];
const SHORT_STAGE = { drawing: 'Drawing', hand: 'Hand Made', color: 'Color Sample' };

export default function DelayReportModal({ open, onClose, devs, showToast }) {
  const [mode, setMode] = useState('des');                  // 'des' | 'fty'
  const [stages, setStages] = useState({
    drawing: { on: true, threshold: DEFAULT_THRESHOLDS.drawing },
    hand:    { on: true, threshold: DEFAULT_THRESHOLDS.hand },
    color:   { on: true, threshold: DEFAULT_THRESHOLDS.color },
  });
  const [selected, setSelected] = useState(() => new Set()); // empty = All
  const [includeImage, setIncludeImage] = useState(true);
  const [summarySheet, setSummarySheet] = useState(true);
  const [oneSheetPerGroup, setOneSheetPerGroup] = useState(false);
  const { run, exporting } = useDelayReportExport();

  // ESC + body scroll lock
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);

  // Empty/blank/non-numeric threshold → NaN, which computeDelayRows treats as
  // "stage off" (skips it) — clearing the input must NOT collapse to 0 (which
  // would dump every dev in that stage). A typed 0 stays a valid threshold.
  const thresholds = useMemo(() => {
    const numT = (v) => { const s = String(v).trim(); if (s === '') return NaN; const n = Number(s); return Number.isFinite(n) ? n : NaN; };
    return {
      drawing: numT(stages.drawing.threshold),
      hand:    numT(stages.hand.threshold),
      color:   numT(stages.color.threshold),
    };
  }, [stages]);
  const stagesOn = useMemo(() => ({ drawing: stages.drawing.on, hand: stages.hand.on, color: stages.color.on }), [stages]);

  const allRows = useMemo(() => computeDelayRows(devs, { thresholds, stagesOn, mode }), [devs, thresholds, stagesOn, mode]);
  const groupList = useMemo(() => groupsFromRows(allRows), [allRows]);
  const filteredRows = useMemo(
    () => (selected.size === 0 ? allRows : allRows.filter(r => selected.has(r.groupKey))),
    [allRows, selected]
  );
  const groupCount = useMemo(() => new Set(filteredRows.map(r => r.groupKey)).size, [filteredRows]);

  const changeMode = (m) => { setMode(m); setSelected(new Set()); };
  const toggleStage = (k) => setStages(s => ({ ...s, [k]: { ...s[k], on: !s[k].on } }));
  const setThreshold = (k, v) => setStages(s => ({ ...s, [k]: { ...s[k], threshold: v } }));
  const toggleChip = (key) => setSelected(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });

  const handleExport = () => {
    if (exporting) return;
    if (filteredRows.length === 0) { showToast && showToast('No delayed developments match the current filters', true); return; }
    run({ rows: filteredRows, mode, options: { includeImage, summarySheet, oneSheetPerGroup } })
      .then(() => {
        showToast && showToast(`Exported ${filteredRows.length} delayed development${filteredRows.length === 1 ? '' : 's'}`);
        onClose && onClose();
      })
      .catch((e) => { showToast && showToast('Export failed: ' + (e?.message || 'unknown'), true); });
  };

  if (!open) return null;

  const preview = filteredRows.slice(0, 8);
  const scopeLbl = mode === 'des' ? 'Designers' : 'Factories';
  const groupCol = mode === 'des' ? 'Designer' : 'Factory';
  const groupNoun = mode === 'des'
    ? (groupCount === 1 ? 'designer' : 'designers')
    : (groupCount === 1 ? 'factory' : 'factories');

  const Switch = ({ on, onClick }) => (
    <div className={`${styles.switch} ${on ? styles.on : ''}`} role="switch" aria-checked={on} onClick={onClick}><i /></div>
  );

  return createPortal(
    <div className={styles.exportRoot} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}>
      <div className={styles.card} role="dialog" aria-label="Export Delay Report">
        {/* Header */}
        <div className={styles.hd}>
          <div className={styles.hdEyebrow}>Developments · Export</div>
          <h1>Delay Report</h1>
          <p>Export delayed developments, grouped by designer or by factory, with images. Built for chasing.</p>
          <button type="button" className={styles.hdClose} onClick={onClose} aria-label="Close">{ICON_CLOSE}</button>
        </div>

        <div className={styles.body}>
          {/* LEFT */}
          <div>
            {/* Mode */}
            <div className={styles.section}>
              <div className={styles.sectionLbl}>Report type</div>
              <div className={styles.seg}>
                <button type="button" className={`${styles.segBtn} ${mode === 'des' ? styles.active : ''}`} onClick={() => changeMode('des')}>
                  By Designer<span className={styles.sub}>delays per designer</span>
                </button>
                <button type="button" className={`${styles.segBtn} ${mode === 'fty' ? styles.active : ''}`} onClick={() => changeMode('fty')}>
                  By Factory<span className={styles.sub}>factory chase-list</span>
                </button>
              </div>
              {mode === 'fty' && (
                <div className={styles.note}>
                  {ICON_INFO}
                  <span><b>Factory mode:</b> only items awaiting a <b>factory reply</b> (Fty reply, 1st drawing, requested…). Items stuck on the designer are excluded — you won't chase a factory for something it hasn't received.</span>
                </div>
              )}
            </div>

            {/* Scope */}
            <div className={styles.section}>
              <div className={styles.sectionLbl}>{scopeLbl}</div>
              <div className={styles.chips}>
                <div className={`${styles.chip} ${styles.chipAll} ${selected.size === 0 ? styles.on : ''}`} onClick={() => setSelected(new Set())}>All</div>
                {groupList.map(g => (
                  <div key={g.key} className={`${styles.chip} ${selected.has(g.key) ? styles.on : ''}`} onClick={() => toggleChip(g.key)}>{g.label}</div>
                ))}
              </div>
            </div>

            {/* Stages + thresholds */}
            <div className={styles.section}>
              <div className={styles.sectionLbl}>Stages &amp; delay threshold</div>
              {STAGE_META.map(s => (
                <div key={s.key} className={`${styles.stageRow} ${stages[s.key].on ? styles.on : ''}`} onClick={() => toggleStage(s.key)}>
                  <div className={styles.stageL}>
                    <div className={styles.chk}>{ICON_CHECK}</div>
                    <div className={`${styles.stageIcon} ${styles[s.cls]}`}><svg viewBox="0 0 24 24">{s.icon}</svg></div>
                    <span className={styles.stageName}>{s.name}</span>
                  </div>
                  <div className={styles.thresh} onClick={(e) => e.stopPropagation()}>
                    <input type="number" min="1" value={stages[s.key].threshold} onChange={(e) => setThreshold(s.key, e.target.value)} />
                    <span>days</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Options */}
            <div className={styles.section}>
              <div className={styles.sectionLbl}>Columns &amp; options</div>
              <div className={styles.togRow}>
                <div className={styles.togInfo}><div className={styles.t}>Include image</div><div className={styles.d}>Embeds =IMAGE(url) — photo shows in the cell</div></div>
                <Switch on={includeImage} onClick={() => setIncludeImage(v => !v)} />
              </div>
              <div className={styles.togRow}>
                <div className={styles.togInfo}><div className={styles.t}>Summary sheet</div><div className={styles.d}>Counts per {mode === 'des' ? 'designer' : 'factory'} × stage, on its own tab</div></div>
                <Switch on={summarySheet} onClick={() => setSummarySheet(v => !v)} />
              </div>
              <div className={styles.togRow}>
                <div className={styles.togInfo}><div className={styles.t}>One sheet per group</div><div className={styles.d}>Separate tab per {mode === 'des' ? 'designer' : 'factory'} (easier to send)</div></div>
                <Switch on={oneSheetPerGroup} onClick={() => setOneSheetPerGroup(v => !v)} />
              </div>
            </div>
          </div>

          {/* RIGHT — preview */}
          <div>
            <div className={styles.sectionLbl}>Live preview <span className={styles.lblMuted}>— what the Excel will contain</span></div>
            <div className={styles.preview}>
              <div className={styles.previewHd}>
                <span>{mode === 'des' ? 'Delayed items · by designer' : 'Factory chase-list · awaiting factory'}</span>
                <span className={styles.live}>LIVE</span>
              </div>
              <table>
                <thead>
                  <tr><th className={styles.imgCell} /><th>Dev #</th><th>{groupCol}</th><th>Stage</th><th>Status</th><th>Days over</th></tr>
                </thead>
                <tbody>
                  {preview.map(r => (
                    <tr key={r.devId}>
                      <td className={styles.imgCell}>
                        {r.imageUrl
                          ? <img src={r.imageUrl} alt="" className={styles.thumb} loading="lazy" />
                          : <div className={styles.ph}>{ICON_IMG}</div>}
                      </td>
                      <td className={styles.dn}>{r.devNumber}</td>
                      <td>{r.groupLabel}</td>
                      <td>{SHORT_STAGE[r.stageKey]}</td>
                      <td>
                        {r.status}
                        {mode === 'des' && (
                          <span className={`${styles.badgeSide} ${r.side === 'factory' ? styles.bFty : styles.bDes}`}>
                            {r.side === 'factory' ? 'Factory' : 'Designer'}
                          </span>
                        )}
                      </td>
                      <td className={styles.over}>{r.daysOver}d</td>
                    </tr>
                  ))}
                  {preview.length === 0 && (
                    <tr><td colSpan={6} className={styles.previewEmpty}>No delayed developments match the current filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className={styles.note} style={{ marginTop: 14 }}>
              {ICON_INFO}
              <span>The preview shows up to 8 rows. The export includes every matching development.</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={styles.ft}>
          <div className={styles.ftMeta}><b>{filteredRows.length}</b> developments match — {groupCount} {groupNoun}</div>
          <div className={styles.ftBtns}>
            <button type="button" className={styles.btnGhost} onClick={onClose}>Cancel</button>
            <button type="button" className={styles.btnPrimary} onClick={handleExport} disabled={exporting || filteredRows.length === 0}>
              {ICON_DOWNLOAD}{exporting ? 'Exporting…' : 'Export Excel'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
